import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarClock, RefreshCw, XCircle, Filter, MessageSquare, Plus, CheckCircle2, Trash2, Bell, User, X } from 'lucide-react'
import api from '../../core/services/api'
import { Toast } from '../../components/Toast'
import ConfirmModal from '../../components/ConfirmModal'
import SchedePerEntita from '../../components/schede/SchedePerEntita'
import { ScadenzaCanone, StatoScadenza, PromemoriaItem, UtenteAssegnabile } from '../../types/scadenziario'

const STATO_COLORS: Record<StatoScadenza, { bg: string; text: string; label: string }> = {
  attiva: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Attiva' },
  rinnovata: { bg: 'bg-green-100', text: 'text-green-700', label: 'Rinnovata' },
  annullata: { bg: 'bg-red-100', text: 'text-red-700', label: 'Annullata' },
}

export default function ScadenziarioPage() {
  const navigate = useNavigate()
  const [scadenze, setScadenze] = useState<ScadenzaCanone[]>([])
  const [promemoria, setPromemoria] = useState<PromemoriaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [deletePromId, setDeletePromId] = useState<string | null>(null)

  // Filtri canoni
  const [filtroStato, setFiltroStato] = useState('attiva')
  const [showAll, setShowAll] = useState(false)

  // Note editing
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')

  // Nuova scadenza (promemoria)
  const [showNewPromemoria, setShowNewPromemoria] = useState(false)
  const [promForm, setPromForm] = useState({
    titolo: '',
    descrizione: '',
    data_scadenza: '',
    giorni_preavviso: 7,
    destinatario_id: '',
  })
  const [creatingProm, setCreatingProm] = useState(false)
  const [utentiAssegnabili, setUtentiAssegnabili] = useState<UtenteAssegnabile[]>([])

  const formatCurrency = (value: number | string | null) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value) || 0)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const loadData = async () => {
    try {
      const params: Record<string, string> = {}
      if (!showAll) {
        params.stato = filtroStato
      } else {
        params.stato = 'attiva,rinnovata,annullata'
      }
      const [canoniRes, promRes] = await Promise.all([
        api.get('/v1/scadenziario', { params }),
        api.get('/v1/promemoria', { params: { stato: 'attivo' } }),
      ])
      setScadenze(canoniRes.data)
      setPromemoria(promRes.data)
    } catch (err) {
      console.error('Errore caricamento scadenze:', err)
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadUtentiAssegnabili = async () => {
    try {
      const res = await api.get('/v1/anagrafiche/utenti-assegnabili?modulo=scadenziario&azione=edit')
      setUtentiAssegnabili(res.data)
    } catch { /* ignore */ }
  }

  useEffect(() => {
    loadData()
    loadUtentiAssegnabili()
  }, [filtroStato, showAll])

  const handleRinnova = async (id: string) => {
    try {
      await api.put(`/v1/scadenziario/${id}/rinnova`)
      loadData()
      setToast({ message: 'Scadenza rinnovata', type: 'success' })
    } catch {
      setToast({ message: 'Errore nel rinnovo', type: 'error' })
    }
  }

  const handleAnnulla = async (id: string) => {
    try {
      await api.put(`/v1/scadenziario/${id}/annulla`)
      loadData()
      setToast({ message: 'Scadenza annullata', type: 'success' })
    } catch {
      setToast({ message: "Errore nell'annullamento", type: 'error' })
    }
  }

  const handleSaveNote = async (id: string) => {
    try {
      await api.put(`/v1/scadenziario/${id}`, { note: noteText })
      setEditingNoteId(null)
      loadData()
    } catch {
      setToast({ message: 'Errore salvataggio note', type: 'error' })
    }
  }

  const handleCreatePromemoria = async () => {
    if (!promForm.titolo || !promForm.data_scadenza || !promForm.destinatario_id) {
      setToast({ message: 'Compila titolo, scadenza e destinatario', type: 'error' })
      return
    }
    setCreatingProm(true)
    try {
      await api.post('/v1/promemoria', promForm)
      setShowNewPromemoria(false)
      setPromForm({ titolo: '', descrizione: '', data_scadenza: '', giorni_preavviso: 7, destinatario_id: '' })
      loadData()
      setToast({ message: 'Scadenza creata', type: 'success' })
    } catch {
      setToast({ message: 'Errore creazione scadenza', type: 'error' })
    } finally {
      setCreatingProm(false)
    }
  }

  const handleCompletaPromemoria = async (id: string) => {
    try {
      await api.put(`/v1/promemoria/${id}/completa`)
      loadData()
      setToast({ message: 'Promemoria completato', type: 'success' })
    } catch {
      setToast({ message: 'Errore', type: 'error' })
    }
  }

  const confirmDeletePromemoria = async () => {
    if (!deletePromId) return
    try {
      await api.delete(`/v1/promemoria/${deletePromId}`)
      loadData()
      setToast({ message: 'Promemoria eliminato', type: 'success' })
    } catch {
      setToast({ message: 'Errore eliminazione', type: 'error' })
    } finally {
      setDeletePromId(null)
    }
  }

  const getGiorniLabel = (giorni: number) => {
    if (giorni < 0) return { text: `Scaduta da ${Math.abs(giorni)} gg`, color: 'text-red-600 font-bold' }
    if (giorni === 0) return { text: 'Scade oggi', color: 'text-red-600 font-bold' }
    if (giorni <= 30) return { text: `Tra ${giorni} gg`, color: 'text-amber-600 font-medium' }
    return { text: `Tra ${giorni} gg`, color: 'text-gray-500' }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <CalendarClock className="w-7 h-7 text-indigo-500" />
          Scadenziario
        </h1>
        <button
          onClick={() => setShowNewPromemoria(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Nuova Scadenza
        </button>
      </div>

      {/* ====== SEZIONE PROMEMORIA ====== */}
      {promemoria.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Promemoria ({promemoria.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {promemoria.map((p) => {
              const giorniInfo = getGiorniLabel(p.giorni_rimanenti)
              const isAuto = p.origine !== 'manuale'
              return (
                <div key={p.id} className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${
                  p.scaduto ? 'border-red-500' : p.in_preavviso ? 'border-amber-500' : 'border-indigo-300'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 truncate">{p.titolo}</h3>
                        {isAuto && (
                          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded font-medium flex-shrink-0">AUTO</span>
                        )}
                        {p.categoria && (
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[10px] rounded font-medium flex-shrink-0">{p.categoria}</span>
                        )}
                      </div>
                      {p.entity_label && (
                        <p className="text-xs text-indigo-500 mt-0.5 truncate">{p.entity_label}</p>
                      )}
                      {p.descrizione && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{p.descrizione}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        <span className="text-gray-500">{formatDate(p.data_scadenza)}</span>
                        <span className={giorniInfo.color}>{giorniInfo.text}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                        <User className="w-3 h-3" />
                        <span>{p.destinatario_nome}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <button
                        onClick={() => handleCompletaPromemoria(p.id)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                        title="Completato"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeletePromId(p.id)}
                        className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {/* Schede dinamiche collegate */}
                  <SchedePerEntita
                    entityType="promemoria"
                    entityId={p.id}
                    entityLabel={p.titolo}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ====== SEZIONE CANONI ====== */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Scadenze Canoni
        </h2>

        {/* Filtri */}
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-4 h-4 text-gray-400" />
          <button
            onClick={() => { setShowAll(false); setFiltroStato('attiva') }}
            className={`px-3 py-1.5 text-sm rounded-lg ${!showAll && filtroStato === 'attiva' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Attive
          </button>
          <button
            onClick={() => { setShowAll(false); setFiltroStato('rinnovata') }}
            className={`px-3 py-1.5 text-sm rounded-lg ${!showAll && filtroStato === 'rinnovata' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Rinnovate
          </button>
          <button
            onClick={() => { setShowAll(false); setFiltroStato('annullata') }}
            className={`px-3 py-1.5 text-sm rounded-lg ${!showAll && filtroStato === 'annullata' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Annullate
          </button>
          <button
            onClick={() => setShowAll(true)}
            className={`px-3 py-1.5 text-sm rounded-lg ${showAll ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Tutte
          </button>
        </div>

        {/* Tabella canoni */}
        {scadenze.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <CalendarClock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nessuna scadenza canone trovata</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Scadenza</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Offerta</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Canone</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase">Importo</th>
                  <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase">Frequenza</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Commerciale</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scadenze.map((s) => {
                  const statoInfo = STATO_COLORS[s.stato] || STATO_COLORS.attiva
                  const giorniInfo = getGiorniLabel(s.giorni_rimanenti)

                  return (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statoInfo.bg} ${statoInfo.text}`}>
                          {statoInfo.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900">{formatDate(s.data_scadenza)}</div>
                        <div className={`text-xs ${giorniInfo.color}`}>{giorniInfo.text}</div>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => navigate(`/app/offerte/${s.offerta_id}`)}
                          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium hover:underline"
                        >
                          {s.numero_offerta}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{s.cliente_nome || '-'}</td>
                      <td className="py-3 px-4 text-sm text-gray-700 max-w-[200px] truncate">{s.descrizione_canone || '-'}</td>
                      <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                        {formatCurrency(s.importo)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-xs text-indigo-600 font-medium">{s.frequenza_label}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">{s.assegnato_nome || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {s.stato === 'attiva' && (
                            <>
                              <button
                                onClick={() => handleRinnova(s.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                                title="Rinnova"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleAnnulla(s.id)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                                title="Annulla"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => {
                              setEditingNoteId(s.id)
                              setNoteText(s.note || '')
                            }}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"
                            title="Note"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Note */}
      {editingNoteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Note Scadenza</h3>
            <textarea
              rows={4}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder="Inserisci note..."
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => setEditingNoteId(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={() => handleSaveNote(editingNoteId)}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nuova Scadenza */}
      {showNewPromemoria && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Nuova Scadenza</h3>
              <button onClick={() => setShowNewPromemoria(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
                <input type="text" value={promForm.titolo}
                  onChange={(e) => setPromForm({ ...promForm, titolo: e.target.value })}
                  placeholder="Es: Rinnovo certificazione ISO"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea value={promForm.descrizione}
                  onChange={(e) => setPromForm({ ...promForm, descrizione: e.target.value })}
                  rows={3} placeholder="Dettagli aggiuntivi..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data scadenza *</label>
                  <input type="date" value={promForm.data_scadenza}
                    onChange={(e) => setPromForm({ ...promForm, data_scadenza: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Giorni preavviso</label>
                  <input type="number" value={promForm.giorni_preavviso} min={1} max={365}
                    onChange={(e) => setPromForm({ ...promForm, giorni_preavviso: parseInt(e.target.value) || 7 })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destinatario *</label>
                <select value={promForm.destinatario_id}
                  onChange={(e) => setPromForm({ ...promForm, destinatario_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                  <option value="">Seleziona...</option>
                  {utentiAssegnabili.map(u => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowNewPromemoria(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Annulla
              </button>
              <button onClick={handleCreatePromemoria} disabled={creatingProm}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {creatingProm ? 'Creazione...' : 'Crea Scadenza'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deletePromId}
        title="Eliminare promemoria"
        message="Eliminare questo promemoria?"
        confirmLabel="Elimina"
        onConfirm={confirmDeletePromemoria}
        onCancel={() => setDeletePromId(null)}
      />
    </div>
  )
}
